import { Box, Flex, IconButton, Image, Text } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'

import IconHeart from '@/components/icons/IconHeart'
import IconDownload from '@/components/icons/IconDownload'
import { downloadFromUrl } from '@/utils/download'
import { useScrollReveal } from '../hooks/use-scroll-reveal'
import { useHoverIntent } from '../hooks/use-hover-intent'
import MockupHoverPanel from './mockup-hover-panel'

import type { Design } from '@/api/designs'
import type { LayoutMode } from '../types'

interface DesignCardProps {
  design: Design
  index: number
  columnsPerRow: number
  layoutMode: LayoutMode
  onImageClick: (design: Design) => void
  onToggleFavorite: (designId: string, isFavorite: boolean) => void
}

function getAspectRatio(layoutMode: LayoutMode): string | undefined {
  if (layoutMode === 'grid') return '1'
  if (layoutMode === 'large') return '16/10'
  return undefined
}

export default function DesignCard({
  design,
  index,
  columnsPerRow,
  layoutMode,
  onImageClick,
  onToggleFavorite
}: DesignCardProps) {
  const { ref, isVisible } = useScrollReveal()
  const {
    isHovered,
    mousePoint,
    onCardMouseEnter,
    onCardMouseLeave,
    onPanelMouseEnter,
    onPanelMouseLeave
  } = useHoverIntent()
  const aspectRatio = getAspectRatio(layoutMode)

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!design.imageUrl) return
    void downloadFromUrl(design.imageUrl, `ablo-design-${Date.now()}`)
  }

  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation()
    onToggleFavorite(design.id, !!design.isFavorite)
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{
        duration: 0.5,
        delay: (index % columnsPerRow) * 0.1,
        ease: 'easeOut'
      }}
      whileHover={{ scale: 1.015 }}
      style={{ cursor: 'pointer' }}
      onMouseEnter={(e) => onCardMouseEnter(e)}
      onMouseLeave={onCardMouseLeave}
    >
      <Box
        borderRadius={layoutMode === 'large' ? '16px' : '12px'}
        overflow="hidden"
        bg="bg.secondary"
        border="1px solid"
        borderColor="border.subtle"
        position="relative"
        role="group"
        transition="box-shadow 0.3s ease"
        _hover={{
          boxShadow:
            '0 8px 40px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.08)'
        }}
        onClick={() => design.imageUrl && onImageClick(design)}
      >
        {design.imageUrl && (
          <Image
            src={design.imageUrl}
            alt={design.name}
            w="100%"
            display="block"
            loading="lazy"
            objectFit="cover"
            aspectRatio={aspectRatio}
          />
        )}

        {/* Hover overlay */}
        <Box
          position="absolute"
          inset="0"
          bgGradient="linear(to-t, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 40%, transparent 100%)"
          opacity={0}
          _groupHover={{ opacity: 1 }}
          transition="opacity 0.3s ease"
          display="flex"
          flexDirection="column"
          justifyContent="flex-end"
          p="16px"
        >
          <Flex justifyContent="space-between" alignItems="center">
            <Text textStyle="bodySmall" color="white" truncate>
              {design.name}
            </Text>
            <Flex gap="8px">
              <IconButton
                aria-label="Toggle favorite"
                w="34px"
                h="34px"
                minW="34px"
                borderRadius="full"
                bg="rgba(255,255,255,0.1)"
                backdropFilter="blur(8px)"
                color={design.isFavorite ? 'red.400' : 'white'}
                _hover={{ bg: 'rgba(255,255,255,0.2)' }}
                transition="all 0.2s ease"
                onClick={handleFavorite}
              >
                <IconHeart filled={!!design.isFavorite} boxSize="16px" />
              </IconButton>
              <IconButton
                aria-label="Download design"
                w="34px"
                h="34px"
                minW="34px"
                borderRadius="full"
                bg="rgba(255,255,255,0.1)"
                backdropFilter="blur(8px)"
                color="white"
                _hover={{ bg: 'rgba(255,255,255,0.2)' }}
                transition="all 0.2s ease"
                onClick={handleDownload}
              >
                <IconDownload boxSize="16px" />
              </IconButton>
            </Flex>
          </Flex>
        </Box>
      </Box>

      <AnimatePresence>
        {isHovered && mousePoint && (
          <MockupHoverPanel
            designId={design.id}
            productCategory={design.productCategory}
            mousePoint={mousePoint}
            onMouseEnter={onPanelMouseEnter}
            onMouseLeave={onPanelMouseLeave}
          />
        )}
      </AnimatePresence>
    </motion.div>
  )
}
